/**
 * HitMarkers — Center-screen crosshair pulse when one of the local player's
 * drones lands a damaging hit on any enemy.
 *
 * Subscribes to `DamageDealt` server events through {@link ClientNetManager}.
 * Pulse is a short CSS-driven animation; we toggle a class and reset on a
 * timer. No WebGL — DOM only so it stays framework-agnostic.
 */
import type { ClientNetManager } from '../networking/ClientNetManager';
import { ServerEvent } from '../../shared/types';
import { shouldIgnoreDamageEvent } from '../game/eventGating';

export interface HitMarkersGateState {
  localSessionId: string;
  localDroneIds: ReadonlySet<string>;
}

const PULSE_MS = 280;

export class HitMarkers {
  private marker: HTMLElement;
  private getGateState: () => HitMarkersGateState;
  private attached = false;
  private resetTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(layer: HTMLElement, getGateState: () => HitMarkersGateState) {
    this.getGateState = getGateState;

    this.marker = document.createElement('div');
    this.marker.className = 'hit-marker';
    this.marker.innerHTML = `
      <span class="hit-marker-line hit-marker-line-tl"></span>
      <span class="hit-marker-line hit-marker-line-tr"></span>
      <span class="hit-marker-line hit-marker-line-bl"></span>
      <span class="hit-marker-line hit-marker-line-br"></span>
    `;
    layer.appendChild(this.marker);
  }

  /**
   * Subscribe to network events. Idempotent: ClientNetManager has no
   * per-callback unsubscribe, so a second call would otherwise stack
   * duplicate handlers. We guard with the `attached` flag.
   */
  attach(net: ClientNetManager): void {
    if (this.attached) return;
    this.attached = true;
    net.onServerEvent((eventType, data) => {
      if (!this.attached) return;
      if (eventType !== ServerEvent.DamageDealt) return;
      if (shouldIgnoreDamageEvent(data)) return;
      const attackerId = data?.attackerDroneId;
      if (typeof attackerId !== 'string') return;
      if (!this.getGateState().localDroneIds.has(attackerId)) return;
      // A miss still produces a marker pulse so the player gets feedback that
      // the shot fired — but in a different (gray-tinted) variant so it's
      // distinguishable from a real hit.
      const missed = (data as { missed?: unknown }).missed === true;
      this.pulse(missed);
    });
  }

  detach(): void {
    this.attached = false;
    if (this.resetTimer !== null) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }
    this.marker.classList.remove('active', 'hit-marker--miss');
  }

  /** No-op (CSS-driven animation); kept for renderer-pattern symmetry. */
  update(_deltaMs: number): void {}

  private pulse(missed: boolean = false): void {
    this.marker.classList.remove('active', 'hit-marker--miss');
    // Force reflow so the animation restarts even on rapid consecutive hits.
    void this.marker.offsetWidth;
    this.marker.classList.add('active');
    // Miss variant — distinct (gray) tint so the player can tell at a glance
    // that the shot whiffed without reading the damage floater.
    if (missed) this.marker.classList.add('hit-marker--miss');

    if (this.resetTimer !== null) clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => {
      this.marker.classList.remove('active', 'hit-marker--miss');
      this.resetTimer = null;
    }, PULSE_MS);
  }
}
